import { Fragment, useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { RefreshButton } from "./RefreshButton";
import { fmt, short } from "../format";
import type { HealingAttempt, HealingBudget, PreviousExtractor, Scraper } from "../types";

/** Render a selector the way both the proposed candidate and the previous version display it. */
function fmtSelector(selector: string, attr: string | null, selectorType: string): string {
  return `${selector}${attr ? ` @${attr}` : ""}${selectorType !== "css" ? ` (${selectorType})` : ""}`;
}

/** One proposed field from the attempt's candidate summary (detail.candidate, written by the healer). */
interface CandidateField {
  name: string;
  selector: string;
  selectorType: string;
  attr: string | null;
  resolvedNonNull: number;
  resolvedTotal: number;
  sample: string | null;
}

/** Defensive read of detail.candidate — the jsonb is worker-written, never trust its shape. */
function candidateOf(detail: Record<string, unknown> | null | undefined): {
  rootSelector: string | null;
  matchedItems: number | null;
  fields: CandidateField[];
} | null {
  const c = detail?.candidate;
  if (!c || typeof c !== "object") return null;
  const o = c as Record<string, unknown>;
  const rawFields = Array.isArray(o.fields) ? o.fields : [];
  return {
    rootSelector: typeof o.rootSelector === "string" ? o.rootSelector : null,
    matchedItems: typeof o.matchedItems === "number" ? o.matchedItems : null,
    fields: rawFields.flatMap((f) => {
      if (!f || typeof f !== "object") return [];
      const x = f as Record<string, unknown>;
      if (typeof x.name !== "string" || typeof x.selector !== "string") return [];
      return [
        {
          name: x.name,
          selector: x.selector,
          selectorType: typeof x.selectorType === "string" ? x.selectorType : "css",
          attr: typeof x.attr === "string" ? x.attr : null,
          resolvedNonNull: typeof x.resolvedNonNull === "number" ? x.resolvedNonNull : 0,
          resolvedTotal: typeof x.resolvedTotal === "number" ? x.resolvedTotal : 0,
          sample: typeof x.sample === "string" ? x.sample : null,
        },
      ];
    }),
  };
}

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);

/** Expanded diagnostics for one attempt — everything the worker recorded in detail. */
function AttemptDetail({ a }: { a: HealingAttempt }) {
  const d = a.detail ?? null;
  const cand = candidateOf(d);
  const errors = Array.isArray(d?.errors) ? (d.errors as unknown[]).filter((e) => typeof e === "string") : [];
  const provider = str(d?.provider);
  const model = str(d?.model);
  const reason = str(d?.reason);
  const dedupedFrom = str(d?.dedupedFrom);
  const newVersionId = str(d?.newVersionId);

  // The "before": fetch the parent of the canary this heal wrote, so the proposed selectors read as a
  // true before→after diff. Runs only on expand (this component mounts when the row opens) and only when
  // a canary was written — a skipped/failed attempt with no newVersionId has nothing to compare.
  const prev = useAsync<PreviousExtractor>(
    () =>
      newVersionId
        ? api(`/healing/versions/${newVersionId}/previous`)
        : Promise.resolve({ previous: null }),
    [newVersionId],
  );
  const previous = prev.data?.previous ?? null;
  const prevByName = new Map(previous?.fields.map((f) => [f.name, f] as const) ?? []);
  const prevRoot = previous?.rootSelector ?? null;
  const removedFields = cand
    ? [...prevByName.values()].filter((pf) => !cand.fields.some((f) => f.name === pf.name))
    : [];
  const rootAfter = cand?.rootSelector ?? null;
  const rootChanged = prevRoot !== null && rootAfter !== null && prevRoot !== rootAfter;
  const showRootRow = !!cand && (rootAfter !== null || prevRoot !== null);

  if (!d) return <div className="muted">No diagnostics recorded for this attempt.</div>;
  return (
    <div className="stack-sm">
      <div className="row-gap wrap muted">
        {provider && model && (
          <span>
            model <b className="mono">{provider}:{model}</b>
          </span>
        )}
        {reason && (
          <span>
            reason <b>{reason}</b>
          </span>
        )}
        {dedupedFrom && (
          <span>
            reused sibling fix <b className="mono">{short(dedupedFrom)}</b>
          </span>
        )}
        {newVersionId && (
          <span>
            wrote canary <b className="mono">{short(newVersionId)}</b>
          </span>
        )}
      </div>

      {cand && (
        <>
          <table className="sub">
            <thead>
              <tr>
                <th>Field</th>
                <th>Before</th>
                <th>Proposed</th>
                <th>Resolved</th>
                <th>Sample</th>
              </tr>
            </thead>
            <tbody>
              {showRootRow && (
                <tr className={rootChanged ? "diff-changed" : undefined}>
                  <td className="muted">(root)</td>
                  <td className="mono was">{prevRoot ?? <span className="muted">—</span>}</td>
                  <td className="mono">
                    {rootAfter ?? <span className="muted">—</span>}
                    {cand.matchedItems !== null ? ` · ${cand.matchedItems} items` : ""}
                  </td>
                  <td />
                  <td />
                </tr>
              )}
              {cand.fields.map((f) => {
                const pf = prevByName.get(f.name);
                const before = pf ? fmtSelector(pf.selector, pf.attr, pf.selectorType) : null;
                const after = fmtSelector(f.selector, f.attr, f.selectorType);
                const changed = before !== null && before !== after;
                return (
                  <tr key={f.name} className={changed ? "diff-changed" : undefined}>
                    <td>{f.name}</td>
                    <td className="mono was">
                      {before ?? <span className="muted">— new</span>}
                    </td>
                    <td className="mono">{after}</td>
                    <td>
                      {f.resolvedNonNull}/{f.resolvedTotal}
                    </td>
                    <td className="muted">{f.sample ?? "—"}</td>
                  </tr>
                );
              })}
              {removedFields.map((pf) => (
                <tr key={`removed-${pf.name}`} className="diff-removed">
                  <td>{pf.name}</td>
                  <td className="mono was">{fmtSelector(pf.selector, pf.attr, pf.selectorType)}</td>
                  <td className="muted">— removed</td>
                  <td />
                  <td />
                </tr>
              ))}
            </tbody>
          </table>
          {newVersionId && prev.loading && (
            <p className="muted sm">Loading previous selectors…</p>
          )}
          {newVersionId && !prev.loading && previous === null && (
            <p className="muted sm">No prior version to compare (first extractor for this scraper).</p>
          )}
        </>
      )}

      {errors.length > 0 && (
        <ul className="muted err-list">
          {(errors as string[]).slice(0, 10).map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Every repair Amendie has attempted — the ledger behind the seams. */
export function HealingEvents() {
  const [auto, setAuto] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const interval = auto ? 10_000 : undefined;
  const { data, loading, error, reload } = useAsync<HealingAttempt[]>(
    () => api("/healing/attempts?limit=100"),
    [],
    interval,
  );
  const budget = useAsync<HealingBudget>(() => api("/healing/budget"), [], interval);
  // /healing/attempts hashes the scraper — an operator reading this needs the name.
  const { data: scrapers } = useAsync<Scraper[]>(() => api("/scrapers"), []);
  const nameOf = (id: string) => scrapers?.find((s) => s.id === id)?.name ?? short(id);

  if (loading && !data) return <div className="muted pad">Loading…</div>;
  if (error) return <div className="err pad">Error: {error}</div>;
  const attempts = data ?? [];
  const b = budget.data;

  return (
    <div className="stack">
      <div className="section-head">
        <h1>Heals</h1>
        <div className="row-gap">
          <label className="inline muted">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} /> auto
          </label>
          <RefreshButton onClick={reload} />
        </div>
      </div>

      {b && (
        <div className="health-bar">
          <span className="hb-item">
            <b>${b.daily.spentUsd.toFixed(4)}</b> spent in {b.daily.windowHours}h / ${b.daily.capUsd}
          </span>
          <span className="hb-item">
            <b>{b.hourly.tokens.toLocaleString()}</b> tokens in {b.hourly.windowHours}h /{" "}
            {b.hourly.capTokens.toLocaleString()}
          </span>
          <span className={b.tripped ? "hb-item bad" : "hb-item"}>
            breaker <b>{b.tripped ? "tripped" : "ok"}</b>
          </span>
        </div>
      )}

      {attempts.length === 0 && <div className="empty">Nothing has needed repairing yet.</div>}

      {attempts.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Scraper</th>
                <th>#</th>
                <th>Status</th>
                <th>Outcome</th>
                <th>Canary</th>
                <th>Cost</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((a) => (
                <Fragment key={a.id}>
                  <tr
                    className={`row-click${a.outcome === "healed" ? " is-heal" : ""}`}
                    onClick={() => setOpen(open === a.id ? null : a.id)}
                    title="Click for diagnostics (proposed selectors, model, errors)"
                  >
                    <td>{nameOf(a.scraperId)}</td>
                    <td>{a.attemptNo}</td>
                    <td>
                      <span className={`pill ${a.status}`}>{a.status}</span>
                    </td>
                    <td>{a.outcome ?? "—"}</td>
                    <td>{a.passedCanary == null ? "—" : a.passedCanary ? "passed" : "failed"}</td>
                    <td className="mono">{a.costUsd == null ? "—" : `$${a.costUsd.toFixed(4)}`}</td>
                    <td className="nowrap">{fmt(a.createdAt)}</td>
                  </tr>
                  {open === a.id && (
                    <tr className="detail-row">
                      <td colSpan={7}>
                        <AttemptDetail a={a} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
